<template>
  <Teleport to="body">
    <cl-modal
      :visible="showModal"
      @on-close="onCloseModal"
      :headerLabel="title"
    >
      <form class="modal-form" @submit.prevent="submit" autocomplete="off">
        <!-- row domain to block domain -->
        <div>
          <cl-form-group>
            <cl-form-label label-for="domain_input">
              {{ inputLabel }}
            </cl-form-label>
            <cl-form-input
              id="domain_input"
              name="domain_input"
              v-model:value="form.domain"
              :state="setInputState(v$.form.domain)"
              @on-blur="v$.form.domain.$touch"
            />
            <div
              class="tw-block tw-text-sm tw-text-danger-500"
              v-if="v$.form.domain.$error"
            >
              {{ $t("You should provide valid domain address") }}
            </div>
          </cl-form-group>
        </div>
        <!-- row include subdomains archive -->
        <div class="tw-mb-3 tw-rounded-md tw-bg-light tw-p-2">
          <cl-checkbox
            id="apply-body-checkbox"
            v-model:model-value="form.include_subdomains"
          >
            {{ $t("Include Subdomains") }}
          </cl-checkbox>
        </div>
        <!-- row comment -->
        <div>
          <cl-form-group>
            <cl-form-label label-for="comment-textarea">
              {{ $t("Comments (optional)") }}
            </cl-form-label>
            <cl-form-textarea
              id="comment-textarea"
              name="comment-textarea"
              v-model:value="form.comment"
              rows="3"
              max-rows="6"
            ></cl-form-textarea>
          </cl-form-group>
        </div>
      </form>

      <template v-slot:[`footer.decline`]>
        <cl-button
          :disabled="fetching"
          @on-click="closeModal"
          variant="link-secondary"
        >
          <span>{{ $t("Cancel") }}</span>
        </cl-button>
      </template>
      <template v-slot:[`footer.accept`]>
        <cl-button v-if="fetching" :variant="'secondary'">
          <cl-spinner :size="'small'" />
        </cl-button>
        <cl-button
          v-else
          :variant="'secondary'"
          @on-click="submit"
          data-test-id="modal-allow-block-add-domain-button"
        >
          <span v-if="isAllow">{{ this.$t("Allow") }}</span>
          <span v-else>{{ this.$t("Block") }}</span>
        </cl-button>
      </template>
    </cl-modal>
  </Teleport>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { REGEX } from "@/constants";
import { useFocusElement } from "@/composables/useFocusElement";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";

const DEFAULT_FORM = Object.freeze({
  domain: "",
  include_subdomains: false,
  comment: "",
});

export default {
  setup() {
    const { focusElementById } = useFocusElement();
    return { v$: useVuelidate(), focusElementById };
  },
  validations() {
    return {
      form: {
        domain: {
          required,
          matchRegex: (value) => REGEX.DOMAIN.test(value),
        },
      },
    };
  },
  props: {
    isAllow: {
      type: Boolean,
      required: true,
    },
    id: {
      type: [String, Number],
      required: true,
    },
    tier: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      requierd: true,
    },
  },
  data() {
    return {
      fetching: false,
      DOMAIN: REGEX.DOMAIN,
      form: {
        ...DEFAULT_FORM,
      },
    };
  },
  computed: {
    ...mapGetters("modal", ["showModal"]),
    inputLabel() {
      return this.$props.isAllow
        ? this.$t("Domain to Allow")
        : this.$t("Domain to Block");
    },
  },
  watch: {
    showModal: {
      handler() {
        this.focusElementById("domain_input");
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions("blockAllowDomainsList", ["blockDomain", "allowDomain"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("modal", ["closeModal"]),
    setInputState(input) {
      return input.$dirty ? !input.$error : null;
    },
    onCloseModal() {
      this.closeModal();
      this.form = { ...DEFAULT_FORM };
      this.v$.$reset();
    },
    async submit() {
      const isValid = await this.v$.form.$validate();

      if (!isValid) {
        return;
      }

      if (this.$props.isAllow) {
        await this.submitAllowDomain();
      } else {
        await this.submitBlockDomain();
      }
    },
    async submitAllowDomain() {
      try {
        this.fetching = true;

        await this.allowDomain({
          entry: this.form,
          tier: this.$props.tier,
          id: this.$props.id,
        });

        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Domain has been allowed", {
            name: this.form.domain,
          }),
          duration: 2000,
          variant: "success",
        });

        this.onCloseModal();
      } catch (_err) {
        // stub
      } finally {
        this.fetching = false;
      }
    },
    async submitBlockDomain() {
      try {
        this.fetching = true;

        await this.blockDomain({
          entry: this.form,
          tier: this.$props.tier,
          id: this.$props.id,
        });

        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Domain has been blocked", {
            domain: this.form.domain,
          }),
          duration: 2000,
          variant: "success",
        });

        this.onCloseModal();
      } catch (_err) {
        // stub
      } finally {
        this.fetching = false;
      }
    },
  },
};
</script>
